import { SpaceBetween, Icon, Button, Header, Alert, Link } from '@amzn/awsui-components-react';
import { IneligibilityReasons } from '@amzn/red-velvet-api';
import { useLocalizedText } from '../../hooks/localizedText';
import { LocalizedTrans } from '../../hooks/localization/localizedTrans';
import { useLocale } from '../../hooks/locale';
import { cloneElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetAmbassadors } from '../../hooks/ambassadors/getAmbassadors';
import { useUser } from '../../hooks/user';
import { AMBASSADOR_EXTERNAL_PLAN_EVENT_LINK } from '../../utils/constants';

const SUPPORT_TICKET_LINK =
  'https://t.corp.amazon.com/create/templates/9cedc919-f013-43b0-9e96-e096bd8bbb5a';

interface AmbassadorRegistrationFailureProps {
  ineligibilityReasons: IneligibilityReasons | undefined;
}

export const AmbassadorRegistrationSuccess = () => {
  const { t } = useLocalizedText(['ambassadors', 'translation']);
  const ambassadors = useGetAmbassadors();
  const loggedInUser = useUser();

  const ambassadorsInArea = ambassadors?.data?.filter((i) => {
    const ambassadorIsInSameCityAsLoggedInUser = i.city === loggedInUser.employeeCity;

    return ambassadorIsInSameCityAsLoggedInUser;
  });

  return (
    <SpaceBetween direction="vertical" size="s" alignItems="center">
      <Icon name="status-positive" size="large" variant="success" />
      <Header data-testid="ambassador-registration-success-header" variant="h1">
        {t('ambassadorRegistration.result.success.successTitle')}
      </Header>
      <Alert
        statusIconAriaLabel="Info"
        header={
          ambassadorsInArea?.length
            ? t('ambassadorRegistration.result.success.ambassadorsInYourAreaWithCount', {
                count: ambassadorsInArea?.length,
              })
            : t('ambassadorRegistration.result.success.ambassadorsInYourArea')
        }
      >
        <SpaceBetween direction="vertical" size="xxxs">
          {t('ambassadorRegistration.result.success.teamUp')}
          <Link href="/ambassadors?in-your-area=true" target="_blank">
            {t('ambassadorRegistration.result.success.viewAmbassadors')}
          </Link>
        </SpaceBetween>
      </Alert>
      <div style={{ justifyItems: 'center' }}>
        <SpaceBetween direction="horizontal" size="s">
          <Button href={AMBASSADOR_EXTERNAL_PLAN_EVENT_LINK} variant="normal" external>
            {t('ambassadorRegistration.result.success.planEvent')}
          </Button>
        </SpaceBetween>
      </div>
    </SpaceBetween>
  );
};

export const AmbassadorRegistrationFailure = ({
  ineligibilityReasons,
}: AmbassadorRegistrationFailureProps) => {
  const { t } = useLocalizedText(['ambassadors', 'translation']);
  // The registration-ineligibility sentence is one tagged message in RedVelvetTranslations and two
  // fragment keys in the i18next source, and the tagged message reuses the id of the first fragment.
  // So the fallback has to read its fragments from i18next directly: asking useLocalizedText for
  // `ifYouBelieve` would hand back the whole sentence, markup and all, in place of the opening clause.
  const { t: fragmentText } = useTranslation(['ambassadors', 'translation']);
  const locale = useLocale();

  const eligibleDate = ineligibilityReasons?.tenure?.eligibleValue;
  // Derived, not state seeded from t() in an effect: the ARB pack resolves after the first render, so
  // a value captured at mount kept the pre-load English string for the whole session.
  const sixMonthsDate = useMemo(() => {
    const unknownDate = t('ambassadorRegistration.result.error.sixMonthsFromTheDate');
    if (!eligibleDate) {
      return unknownDate;
    }
    try {
      const date = new Date(eligibleDate);
      // An unparseable value formats as the literal "Invalid Date" rather than throwing, which would
      // read as a date to the volunteer. Name the tenure rule instead, as when the date is absent.
      return Number.isNaN(date.getTime())
        ? unknownDate
        : date.toLocaleString(locale, { year: 'numeric', day: 'numeric', month: 'long' });
    } catch (error) {
      console.warn(error);
      return unknownDate;
    }
  }, [eligibleDate, locale, t]);

  const alertIneligibilityReasons = () => {
    const alertValues = {
      employmentStatus: {
        title: t('ambassadorRegistration.result.error.employmentStatus'),
        action: t('ambassadorRegistration.result.error.submitOnceYouveMetEmploymentStatus'),
      },
      badgeColor: {
        title: t('ambassadorRegistration.result.error.badgeColor'),
        action: t('ambassadorRegistration.result.error.youNeedBlueBadge'),
      },
      jobLevel: {
        title: t('ambassadorRegistration.result.error.jobLevel'),
        action: t('ambassadorRegistration.result.error.jobLevelNotHighEnough'),
      },
      tenure: {
        title: t('ambassadorRegistration.result.error.reapplyIn', {
          reapplyDate: sixMonthsDate,
        }),
        action: t('ambassadorRegistration.result.error.submitOnceYouveMetTenure'),
      },
    };

    if (!ineligibilityReasons) return null;

    return Object.keys(ineligibilityReasons as object).map((key, index) => {
      return (
        <li key={index}>
          <b>{alertValues[key as keyof IneligibilityReasons]?.title}</b> -{' '}
          {alertValues[key as keyof IneligibilityReasons]?.action}
        </li>
      );
    });
  };

  const whatYouCanDoListItems = () => {
    const listItemValues = {
      employmentStatus: {
        title: t('ambassadorRegistration.result.error.employmentStatus'),
        action: t('ambassadorRegistration.result.error.submitOnceYouveMetEmploymentStatus'),
      },
      badgeColor: {
        title: t('ambassadorRegistration.result.error.badgeColor'),
        action: t('ambassadorRegistration.result.error.submitOnceBlueBadgeAndManagerApproves'),
      },
      jobLevel: {
        title: t('ambassadorRegistration.result.error.jobLevel'),
        action: t('ambassadorRegistration.result.error.submitOnceL3AndManagerApproves'),
      },
      tenure: {
        title: t('ambassadorRegistration.result.error.reapplyIn', {
          reapplyDate: sixMonthsDate,
        }),
        action: t('ambassadorRegistration.result.error.submitOnceTenureAndManagerApproves'),
      },
    };

    if (!ineligibilityReasons) return null;

    return Object.keys(ineligibilityReasons as object).map((key, index) => {
      return (
        <li key={index}>
          <b>{listItemValues[key as keyof IneligibilityReasons]?.title}</b>
          <br />
          {listItemValues[key as keyof IneligibilityReasons]?.action}
        </li>
      );
    });
  };

  // Declared once so the tagged message and the fragment fallback render the same link: the ARB
  // formatter supplies the label from inside <link>…</link>, hence no children here.
  const supportToolLink = <Link href={SUPPORT_TICKET_LINK} target="_blank" />;

  return (
    <SpaceBetween direction="vertical" size="s" data-ambassador-registration-failure-container>
      <SpaceBetween direction="vertical" size="s" alignItems="center">
        <Icon name="status-warning" size="large" variant="error" />
        <Header variant="h1">{t('ambassadorRegistration.result.error.errorTitle')}</Header>
        <p>{t('ambassadorRegistration.result.error.errorDetail')}</p>
        <Alert
          data-testid="ambassador-registration-failure-alert"
          statusIconAriaLabel="Warning"
          type="warning"
          header={t('ambassadorRegistration.result.error.followingReasons')}
        >
          <SpaceBetween direction="vertical" size="xxxs">
            <ul style={{ paddingLeft: 0 }}>{alertIneligibilityReasons()}</ul>
          </SpaceBetween>
        </Alert>
        <p>
          <LocalizedTrans
            i18nKey="ambassadors:ambassadorRegistration.result.error.ifYouBelieve"
            components={{ link: supportToolLink }}
            fallback={() => (
              <>
                {fragmentText('ambassadorRegistration.result.error.ifYouBelieve')}
                {cloneElement(
                  supportToolLink,
                  {},
                  fragmentText('ambassadorRegistration.result.error.supportTool')
                )}
              </>
            )}
          />
        </p>
      </SpaceBetween>
      <div style={{ width: '100%' }}>
        <Header variant="h3">{t('ambassadorRegistration.result.error.whatYouCanDo')}</Header>
        <ol style={{ paddingLeft: 0, listStyle: 'outside inside', padding: '0 16px' }}>
          {whatYouCanDoListItems()}
        </ol>
      </div>
    </SpaceBetween>
  );
};
